import { defineComponent, ref, nextTick } from 'vue-demi';
import { autoUpdate, computePosition, offset, hide, flip, shift } from '@floating-ui/dom';
import { s as script$1 } from './OrTeleport.vue3-9bce0c02.js';
import { e as getMaxZIndex, d as getCS } from './dom-f1d55b67.js';
import { resolveComponent, openBlock, createBlock, withCtx, createElementVNode, renderSlot } from 'vue';
import { s as styleInject } from './style-inject.es-4c6f2515.js';

var OrFloatingPlacements;
(function (OrFloatingPlacements) {
  OrFloatingPlacements["TOP_START"] = "top-start";
  OrFloatingPlacements["RIGHT_START"] = "right-start";
  OrFloatingPlacements["BOTTOM_START"] = "bottom-start";
  OrFloatingPlacements["LEFT_START"] = "left-start";
  OrFloatingPlacements["TOP"] = "top";
  OrFloatingPlacements["RIGHT"] = "right";
  OrFloatingPlacements["BOTTOM"] = "bottom";
  OrFloatingPlacements["LEFT"] = "left";
  OrFloatingPlacements["TOP_END"] = "top-end";
  OrFloatingPlacements["RIGHT_END"] = "right-end";
  OrFloatingPlacements["BOTTOM_END"] = "bottom-end";
  OrFloatingPlacements["LEFT_END"] = "left-end";
})(OrFloatingPlacements || (OrFloatingPlacements = {}));
var OrFloatingHideStrategy;
(function (OrFloatingHideStrategy) {
  OrFloatingHideStrategy["REFERENCE_HIDDEN"] = "referenceHidden";
  OrFloatingHideStrategy["ESCAPED"] = "escaped";
})(OrFloatingHideStrategy || (OrFloatingHideStrategy = {}));

var script = defineComponent({
  name: 'OrFloating',
  components: {
    OrTeleport: script$1
  },
  props: {
    trigger: {
      type: HTMLElement,
      required: false,
      default: null
    },
    placement: {
      type: String,
      required: false,
      default: OrFloatingPlacements.BOTTOM
    },
    offset: {
      type: Number,
      required: false,
      default: 0
    },
    isInheritingWidth: {
      type: Boolean,
      required: false,
      default: false
    },
    isHidePadded: {
      type: Boolean,
      required: false,
      default: false
    },
    hideStrategy: {
      type: String,
      required: false,
      default: OrFloatingHideStrategy.REFERENCE_HIDDEN
    },
    isUsingFlip: {
      type: Boolean,
      required: false,
      default: false
    },
    isUsingShift: {
      type: Boolean,
      required: false,
      default: false
    },
    zIndexOffset: {
      type: Number,
      required: false,
      default: 0
    }
  },
  setup() {
    const content = ref();
    return {
      content
    };
  },
  data() {
    return {
      isInitialized: false,
      cleanup: null,
      isFlipped: false
    };
  },
  computed: {
    isVertical() {
      return this.placement ? this.placement.includes(OrFloatingPlacements.BOTTOM) || this.placement.includes(OrFloatingPlacements.TOP) : true;
    }
  },
  watch: {
    trigger(newVal, oldVal) {
      if (this.content) {
        if (!oldVal && newVal && !this.isInitialized) {
          this.initialize();
        } else if (newVal !== oldVal && this.isInitialized && this.cleanup) {
          this.cleanup();
          this.initialize();
        }
      }
    },
    content(val) {
      if (val && this.trigger && !this.isInitialized) this.initialize();
    }
  },
  async mounted() {
    if (!this.content) {
      //ref to content is undefined in Vue 2
      await nextTick();
      this.$forceUpdate();
    }
  },
  beforeUnmount() {
    if (this.cleanup) this.cleanup();
  },
  methods: {
    async initialize() {
      this.cleanup = autoUpdate(this.trigger, this.content, this.updatePosition);
      let maxZIndex = getMaxZIndex(this.trigger) + this.zIndexOffset;
      if (maxZIndex) Object.assign(this.content.style, {
        zIndex: maxZIndex
      });
      this.isInitialized = true;
      await this.updatePosition();
    },
    async updatePosition() {
      var _a, _b, _c;
      const {
        x,
        y,
        middlewareData
      } = await computePosition(this.trigger, this.content, {
        placement: this.placement,
        middleware: [offset(this.offset), hide({
          strategy: this.hideStrategy
        }), ...(this.isUsingFlip ? [flip({
          crossAxis: !this.isUsingShift
        })] : []), ...(this.isUsingShift ? [shift()] : [])]
      });
      this.isFlipped = this.isUsingFlip && ((_c = (_b = (_a = middlewareData.flip) === null || _a === void 0 ? void 0 : _a.overflows) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0) > 0;
      const {
        escaped,
        referenceHidden
      } = middlewareData.hide;
      const hidden = this.hideStrategy === OrFloatingHideStrategy.ESCAPED ? escaped : referenceHidden;
      Object.assign(this.content.style, {
        left: `${x}px`,
        top: `${y}px`,
        visibility: hidden ? 'hidden' : 'visible'
      });
      if (this.isInheritingWidth) {
        const cs = getCS(this.trigger);
        const width = cs.width;
        Object.assign(this.content.style, {
          width: width
        });
      }
    }
  }
});

const _hoisted_1 = {
  ref: "content",
  class: "or-floating"
};
function render(_ctx, _cache, $props, $setup, $data, $options) {
  const _component_or_teleport = resolveComponent("or-teleport");
  return openBlock(), createBlock(_component_or_teleport, null, {
    default: withCtx(() => [createElementVNode("div", _hoisted_1, [renderSlot(_ctx.$slots, "default")], 512 /* NEED_PATCH */)]),

    _: 3 /* FORWARDED */
  });
}

var css_248z = ".or-floating[data-v-059a0010] {\n  position: absolute;\n  z-index: 1;\n}";
styleInject(css_248z);

script.render = render;
script.__scopeId = "data-v-059a0010";
script.__file = "src/components/or-floating/OrFloating.vue";

export { OrFloatingPlacements as O, OrFloatingHideStrategy as a, script as s };
